import { useState, useEffect } from 'react';
import { Monitor, MonitorStatus } from '../types';
import { useMonitors } from '../hooks/useMonitors';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../contexts/AuthContext';
import ViewerDashboard from '../components/ViewerDashboard';
import MonitorCard from '../components/MonitorCard';
import { Plus, Activity, Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { formatUptime, formatResponseTime } from '../utils/api';
import { useSearchParams } from 'react-router';
import MonitorModal from '../components/MonitorModal';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const {
    monitors,
    monitorStatuses,
    loading,
    error,
    refreshMonitors,
    refreshStatuses,
    createMonitor,
    updateMonitor,
    deleteMonitor,
  } = useMonitors({ autoRefresh: true, refreshInterval: 30000, enabled: isAdmin });

  const { isConnected } = useSocket();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMonitor, setEditingMonitor] = useState<Monitor | null>(null);

  const [searchParams] = useSearchParams();

  // Handle URL parameters for editing
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && monitors.length > 0) {
      const monitorToEdit = monitors.find(m => m.id === parseInt(editId));
      if (monitorToEdit) {
        setEditingMonitor(monitorToEdit);
        setShowAddModal(true);
      }
    }
  }, [searchParams, monitors]);

  // Effect to fetch monitors and statuses
  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       setLoading(true);
  //       const [monitors, statuses] = await Promise.all([
  //         monitorApi.getMonitors(),
  //         monitorApi.getStatuses(),
  //       ]);
  //       setMonitors(monitors);
  //       setMonitorStatuses(statuses);
  //     } catch (err) {
  //       setError('Failed to fetch monitors or statuses');
  //       console.error(err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   const checkSocketConnection = () => {
  //     setIsConnected(true);
  //   };

  //   fetchData();
  //   const socket = new WebSocket('ws://localhost:8000/ws/status/');

  //   socket.onopen = () => {
  //     console.log('WebSocket connected');
  //     checkSocketConnection();
  //   };

  //   socket.onerror = (error) => {
  //     console.error('WebSocket error:', error);
  //     setIsConnected(false);
  //   };

  //   socket.onmessage = (event) => {
  //     try {
  //       const data = JSON.parse(event.data);
  //       if (data.type === 'monitor_status') {
  //         setMonitorStatuses((prev) =>
  //           prev.map((status) =>
  //             status.monitor_id === data.monitor_id ? { ...status, ...data.status } : status
  //           )
  //         );
  //       }
  //     } catch (e) {
  //       console.error('Failed to parse WebSocket message:', e);
  //     }
  //   };

  //   return () => {
  //     socket.close();
  //   };
  // }, []);

  // Calculate system statistics
  const stats = {
    activeMonitors: monitors.filter((m: Monitor) => m.is_active).length,
    upMonitors: monitorStatuses.filter((s: MonitorStatus) => s.is_currently_up).length,
    downMonitors: monitorStatuses.filter((s: MonitorStatus) => !s.is_currently_up).length,
    overallUptime:
      monitorStatuses.length > 0
        ? Math.round(
            monitorStatuses.reduce((sum: number, s: MonitorStatus) => sum + (s.uptime_percentage || 0), 0) /
              monitorStatuses.length
          )
        : 0,
    averageResponseTime:
      monitorStatuses.length > 0
        ? Math.round(
            monitorStatuses.reduce((sum: number, s: MonitorStatus) => sum + (s.average_response_time || 0), 0) /
              monitorStatuses.length
          )
        : 0,
  };

  const handleAddMonitor = () => {
    setEditingMonitor(null);
    setShowAddModal(true);
  };

  const handleEditMonitor = (monitor: Monitor) => {
    setEditingMonitor(monitor);
    setShowAddModal(true);
  };

  const handleSubmit = async (formData: any) => {
    try {
      console.log('Submitting monitor data:', { editingMonitor, formData });
      
      if (editingMonitor) {
        console.log('Updating monitor:', editingMonitor.id);
        const updatedMonitor = await updateMonitor(editingMonitor.id, formData);
        console.log('Monitor updated successfully:', updatedMonitor);
      } else {
        console.log('Creating new monitor');
        const newMonitor = await createMonitor(formData);
        console.log('Monitor created successfully:', newMonitor);
      }
      
      setShowAddModal(false);
      setEditingMonitor(null);
    } catch (error) {
      console.error('Failed to save monitor:', error);
      // Don't close modal on error so user can fix and retry
    }
  };

  const handleDeleteMonitor = async (id: number) => {
    try {
      await deleteMonitor(id);
    } catch (error) {
      console.error('Failed to delete monitor:', error);
    }
  };



  if (!isAdmin) {
    return <ViewerDashboard />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading monitors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">PingPal Dashboard</h1>
          <p className="text-base-content/70">Monitor your services in real-time</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Connection status */}
          <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            isConnected ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
          }`}>
            {isConnected ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
            <span className="text-sm font-medium">
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          {/* Refresh button */}
          <button
            onClick={() => {
              refreshMonitors();
              refreshStatuses();
            }}
            className="btn btn-outline btn-sm"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>

          {/* Add monitor button */}
          <button
            onClick={handleAddMonitor}
            className="btn btn-primary btn-sm"
          >
            <Plus className="w-4 h-4" />
            Add Monitor
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="alert alert-error mb-6">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-primary">
            <Activity className="w-8 h-8" />
          </div>
          <div className="stat-title">Total Monitors</div>
          <div className="stat-value text-primary">{monitors.length}</div>
          <div className="stat-desc">{stats.activeMonitors} active</div>
        </div>

        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-success">
            <Activity className="w-8 h-8" />
          </div>
          <div className="stat-title">Services Up</div>
          <div className="stat-value text-success">{stats.upMonitors}</div>
          <div className="stat-desc">{stats.downMonitors} down</div>
        </div>

        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-info">
            <Activity className="w-8 h-8" />
          </div>
          <div className="stat-title">Overall Uptime</div>
          <div className="stat-value text-info">{formatUptime(stats.overallUptime)}</div>
          <div className="stat-desc">Average across all monitors</div>
        </div>

        <div className="stat bg-base-100 shadow-lg rounded-lg">
          <div className="stat-figure text-warning">
            <Activity className="w-8 h-8" />
          </div>
          <div className="stat-title">Avg Response</div>
          <div className="stat-value text-warning">{formatResponseTime(stats.averageResponseTime)}</div>
          <div className="stat-desc">Average response time</div>
        </div>
      </div>

      {/* Monitors Grid */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">Monitors</h2>
          <div className="text-sm text-base-content/70">
            {monitors.length} monitor{monitors.length !== 1 ? 's' : ''}
          </div>
        </div>

        {monitors.length === 0 ? (
          <div className="text-center py-12 bg-base-200 rounded-lg">
            <Activity className="w-12 h-12 mx-auto mb-4 text-base-content/50" />
            <h3 className="text-lg font-semibold mb-2">No monitors yet</h3>
            <p className="text-base-content/70 mb-4">
              Add your first monitor to start tracking uptime and performance
            </p>
            <button
              onClick={handleAddMonitor}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4" />
              Add Your First Monitor
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {monitors.map((monitor: Monitor) => (
              <MonitorCard
                key={monitor.id}
                monitor={monitor}
                status={monitorStatuses.find((s) => s.monitor.id === monitor.id)}
                onEdit={handleEditMonitor}
                onDelete={handleDeleteMonitor}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Monitor Modal */}
			<MonitorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleSubmit}
        monitor={editingMonitor}
        title={editingMonitor ? 'Edit Monitor' : 'Add New Monitor'}
      />
    </div>
  );
};

export default Dashboard; 